import { type AxiosResponse } from 'axios';
import moment from 'moment';

import { CPLAT_API_ROUTES, useFetch } from '@/api';
import Skeleton from '@/components/atoms/Skeleton';
import EmptyFaceIcon from '@/assets/icons/EmptyFaceIcon.svg?react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/molecules/Accordion';
import { useAppSelector } from '@/redux/store';
import { useNavigate } from 'react-router-dom';

import { ChevronLeft } from 'lucide-react';
import BadgeSquare from '@/components/atoms/BadgeSquare';
import { Button } from '@/components/atoms/NewPcButton';
import { restoreNewline } from '@/utils/restore-new-line';
const StatusBox = ({ status }: { status: string }) => {
  switch (status) {
    case 'pending':
      return (
        <BadgeSquare size="registerBadge" variant="muted">
          답변 대기
        </BadgeSquare>
      );
    case 'success':
      return (
        <BadgeSquare size="registerBadge" variant="purple">
          답변 완료
        </BadgeSquare>
      );
  }
};

type InquiryHistoryProps = {
  askAnswerList: { Item1: Ask; Item2?: Answer | null }[];
};

const InquiryHistory = ({ askAnswerList }: InquiryHistoryProps) => {
  return askAnswerList.map(({ Item1: ask, Item2: answer }) => {
    const isAnswer = !!answer;

    return (
      <AccordionItem key={ask.AskNo} value={`${ask.AskNo}`} className="border-b border-gr-200">
        <AccordionTrigger className="py-[20px] hover:no-underline">
          <div className="flex w-full items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <StatusBox status={isAnswer ? 'success' : 'pending'} />
              <p className="text-gr-800 Body2M16">{ask.AskTitle}</p>
            </div>
            <p className="mr-[12px] text-gr-700 Body5M14">
              {moment.utc(ask.AskDate).format('YYYY.MM.DD')}
            </p>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <ol className=" rounded-[10px] bg-gr-50 p-[16px]">
            <li className="pb-[10px]">
              <p className="text-gr-800 Body5M14">{ask.AskContext}</p>
            </li>
            {isAnswer && (
              <li className="border-t-[1px]">
                <p className="whitespace-pre-wrap text-gr-800 Body5M14">
                  {restoreNewline(answer.AnswerContext)}
                </p>
              </li>
            )}
          </ol>
        </AccordionContent>
      </AccordionItem>
    );
  });
};

type InquiryHistorys = {
  list: GetAsk[];
  handleBack: () => void;
};

const InquiryHistorys = ({ list, handleBack }: InquiryHistorys) => {
  if (list.length === 0)
    return (
      <div className=" mt-10 flex flex-col items-center justify-center gap-5 text-gr-800">
        <EmptyFaceIcon className="h-11 w-11" />
        <p className="text-gr-700 Body1S16">문의 내역이 없습니다.</p>
        <Button variant="secondary" className="px-[35px]" onClick={handleBack}>
          1:1 문의 작성하기
        </Button>
      </div>
    );

  const askAnswerList = list[0]?.AskAnswerList;

  return (
    <Accordion className="" collapsible type="single">
      <InquiryHistory askAnswerList={askAnswerList} />
    </Accordion>
  );
};

const InquiryHistoryPage = ({ setSelectHistory }) => {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.user);
  const { data, isLoading } = useFetch<AxiosResponse<GetAsk[], unknown>>(
    CPLAT_API_ROUTES.getInquiry,
    {
      method: 'POST',
      params: {
        cplatToken: user.CplatToken,
        orderByMethod: 0,
        skip: 0,
        take: 50,
        serviceType: '셀러박스',
      },
    },
  );
  const handleBack = () => {
    setSelectHistory(false);
  };

  return (
    <div className="flex h-full w-full flex-col items-start justify-start gap-[20px]   py-[20px]">
      <div onClick={handleBack} className="flex cursor-pointer gap-1 text-gr-900 Body1S16">
        <ChevronLeft className="h-6 w-6 text-gr-800" /> 문의내역
      </div>
      <div className="flex w-full  flex-col ">
        {!data?.data || isLoading ? (
          <Skeleton className="h-[60vh] w-full" />
        ) : (
          <InquiryHistorys list={data.data} handleBack={handleBack} />
        )}
      </div>
    </div>
  );
};

export default InquiryHistoryPage;
